import { inject } from 'tsyringe'

import { containerScoped } from '~/decorators/dependency-container'
import { ActionResult } from '~/models/shared/result'
import { Ip } from '~/models/ip'
import AccessTogglesService from '~/services/user/AccessTogglesService'
import { AccessToggleName } from '~/models/user/access-toggles'
import RequestBuilder from '~/builders/http/RequestBuilder'
import { formatClassifiedReportResult } from '~/services/report/classified/formatters'
import { ClassifiedReportsResult } from '~/models/report/classified'
import { SchemaResult } from '~/models/classified/report'
import { formatSchemaField } from '~/services/formatters'
import { ClassifiedReportReason } from '~/models/report/classified/types'

@containerScoped()
export default class ClassifiedReportService {
  constructor(
    @inject(AccessTogglesService)
    private accessTogglesService: AccessTogglesService,
    @inject(RequestBuilder) private requestBuilder: RequestBuilder
  ) {}

  getSchema(classifiedId: number, params: object = {}): Promise<SchemaResult> {
    return this.requestBuilder
      .request('get', `/api/classifieds/${classifiedId}/report/`)
      .params(params)
      .map(body => this.formatSchemaResponse(body.data))
      .send()
  }

  getReports(
    classifiedId: number,
    page: number
  ): Promise<ClassifiedReportsResult> {
    return this.requestBuilder
      .request('get', `/api/admin/classifieds/${classifiedId}/reports/`)
      .params({ pg: page })
      .map(body => formatClassifiedReportResult(body.data.reports))
      .send()
  }

  addBackToReported(classifiedId: number): Promise<ActionResult> {
    return this.requestBuilder
      .request('post', `/api/admin/classifieds/${classifiedId}/reports/`)
      .action()
      .send()
  }

  deleteReports(classifiedId: number): Promise<ActionResult> {
    return this.requestBuilder
      .request('delete', `/api/admin/classifieds/${classifiedId}/reports/`)
      .action()
      .send()
  }

  informUser(reportId: number): Promise<ActionResult> {
    return this.requestBuilder
      .request('post', `/api/admin/classifieds/reports/inform/${reportId}/`)
      .send()
  }

  banIpFromReporting(ip: Ip): Promise<ActionResult> {
    return this.accessTogglesService.disableAccessToggle(
      ip,
      AccessToggleName.REPORT_CLASSIFIEDS
    )
  }

  unbanIpFromReporting(ip: Ip): Promise<ActionResult> {
    return this.accessTogglesService.enableAccessToggle(
      ip,
      AccessToggleName.REPORT_CLASSIFIEDS
    )
  }

  reportClassified(
    classifiedId: number,
    {
      reason,
      details
    }: { reason: ClassifiedReportReason; details: string | null } = {
      reason: ClassifiedReportReason.OTHER,
      details: null
    }
  ): Promise<ActionResult> {
    return (
      this.requestBuilder
        .request('post', `/api/classifieds/${classifiedId}/report/`)
        // have to pass undefined to details so that it is not included in the final request
        .data({ reason, details: details || undefined })
        .action()
        .send()
    )
  }

  private formatSchemaResponse(r: any): SchemaResult {
    const { details, reason } = r.schema
    return {
      schema: {
        details: details && formatSchemaField(details),
        reason: reason && formatSchemaField(reason)
      }
    }
  }
}
